import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ReactECharts from 'echarts-for-react';
import { Icon } from '../../components/Icon';
import { resultApi } from '../../api/client';

interface DeckResult {
  title: string;
  total: number;
  answered: number;
  correct: number;
  deep?: number;
}

// 前端逐题答题时，用 sessionStorage 累积套题成绩（跨组件实例）
const STORAGE_KEY = 'kc_deck_result';

function readLocalResult(): DeckResult | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw) as DeckResult;
    if (!obj || typeof obj.total !== 'number') return null;
    return obj;
  } catch {
    return null;
  }
}

export function ResultPage() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const [serverResult, setServerResult] = useState<DeckResult | null>(null);
  const [loading, setLoading] = useState(false);

  // 优先前端累积结果（sessionStorage），否则回退到服务端 attempt 结算（未来链路）
  const local = useMemo(readLocalResult, []);
  const attemptId = sp.get('attemptId') || '';

  useEffect(() => {
    if (local) return;
    if (!attemptId || attemptId === 'finish') return;
    setLoading(true);
    resultApi.getAttempt(attemptId)
      .then(data => {
        setServerResult({
          title: data.deckTitle || '套题',
          total: data.totalQuestions || 0,
          answered: data.answered || 0,
          correct: data.correctCount || 0,
          deep: data.deepCount || 0,
        });
      })
      .catch(() => setServerResult(null))
      .finally(() => setLoading(false));
  }, [local, attemptId]);

  const result = local || serverResult;

  if (loading) return <div className="loading-state"><span className="spinner" /></div>;

  if (!result) {
    return (
      <div className="page" style={{ textAlign: 'center', padding: 40 }}>
        <div className="empty-state">
          <div className="empty-state-icon"><Icon name="target" size={48} color="var(--gray-300)" /></div>
          <p className="empty-state-text">暂无本次答题成绩</p>
        </div>
        <button className="btn-primary" onClick={() => navigate('/')} style={{ marginTop: 16, padding: '12px 24px' }}>返回首页</button>
      </div>
    );
  }

  const answered = result.answered || result.total || 0;
  const correct = result.correct || 0;
  // 深度知识题不计对错：从答错与正确率基数中剔除（如 54 题含 2 道深度 → 51÷52）
  const deep = result.deep || 0;
  const graded = Math.max(answered - deep, 0);
  const wrong = Math.max(graded - correct, 0);
  const rate = graded > 0 ? (correct / graded) * 100 : 0;

  const level = rate >= 90
    ? { icon: 'trophy' as const, title: '优秀', color: '#f59e0b' }
    : rate >= 75
      ? { icon: 'check' as const, title: '良好', color: '#059669' }
      : rate >= 60
        ? { icon: 'target' as const, title: '合格', color: '#2563eb' }
        : { icon: 'fire' as const, title: '继续加油', color: '#dc2626' };

  const gaugeOption = {
    series: [{
      type: 'gauge', startAngle: 210, endAngle: -30, min: 0, max: 100,
      radius: '100%', center: ['50%', '55%'],
      progress: { show: true, width: 14, itemStyle: { color: level.color } },
      axisLine: { lineStyle: { width: 14, color: [[1, '#eef2ff']] } },
      axisTick: { show: false }, splitLine: { show: false }, axisLabel: { show: false },
      pointer: { show: false }, anchor: { show: false },
      detail: {
        valueAnimation: true, offsetCenter: [0, '-8%'],
        formatter: '{value}%', color: level.color, fontSize: 30, fontWeight: 700,
      },
      data: [{ value: Math.round(rate) }],
    }],
  };

  return (
    <div className="page">
      <div className="result-card">
        <div style={{ width: 160, height: 130, margin: '0 auto 8px' }}>
          <ReactECharts option={gaugeOption} style={{ height: 130 }} notMerge lazyUpdate />
        </div>
        <h1 style={{ color: level.color, fontSize: 26, textAlign: 'center', margin: '4px 0' }}>
          <Icon name={level.icon} size={26} color={level.color} filled /> {level.title}
        </h1>
        {result.title && (
          <p style={{ color: 'var(--gray-500)', marginTop: 4, fontSize: 13, textAlign: 'center' }}>
            套题 {result.title}
          </p>
        )}

        <div className="result-stats">
          <div className="result-stat">
            <div className="result-stat-num">{answered}</div>
            <div className="result-stat-label">已答</div>
          </div>
          <div className="result-stat">
            <div className="result-stat-num" style={{ color: 'var(--color-success)' }}>{correct}</div>
            <div className="result-stat-label">答对</div>
          </div>
          <div className="result-stat">
            <div className="result-stat-num" style={{ color: 'var(--color-danger)' }}>{wrong}</div>
            <div className="result-stat-label">答错</div>
          </div>
        </div>

        <p className="result-hint">
          正确率 {rate.toFixed(1)}% · 已答 {answered}/{result.total || '?'} 题
        </p>
        {deep > 0 && (
          <p style={{ marginTop: 4, fontSize: 12, color: 'var(--gray-400)', textAlign: 'center' }}>
            其中 {deep} 道深度知识题不计对错
          </p>
        )}

        <div className="result-actions">
          <button onClick={() => navigate('/')} className="btn-primary" style={{ flex: 1 }}>继续答题</button>
          <button onClick={() => navigate('/wrong-book')} className="btn-secondary" style={{ flex: 1 }}>查看错题</button>
        </div>
      </div>
    </div>
  );
}
